import React from 'react';

import { SORTABLE_ANIM } from '@/nova/theme/tokens';
import { CONTROLS, LONG_PRESS_SLOP_PX, scrollParentOf, scrollTopOf } from '../../blog-editor/hooks/useSortableRows';
import type { ScreenInsets } from '../game-list-geometry';

/** What a sortable grid needs to wire its slots, its container and its custom drag element. */
export type SortableGrid = {
  /** The id being dragged, for the dimmed-placeholder styling + resolving the drag ghost. */
  draggedId: string | null;
  /** Ref callback per slot: every slot element must be registered for the FLIP glide + drop math. */
  registerSlot: (id: string) => (el: HTMLDivElement | null) => void;
  /** The grid container's `onDragOver`: realtime reorder driven by the pointer. */
  onGridDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  /** Drag handlers for one slot (spread on the draggable element). */
  slotDragProps: (id: string) => {
    onDragStart: (event: React.DragEvent<HTMLDivElement>) => void;
    onDrag: (event: React.DragEvent<HTMLDivElement>) => void;
    onDragEnd: () => void;
    /** `longPress` only: a finger resting on the slot lifts it. */
    onPointerDown?: (event: React.PointerEvent<HTMLDivElement>) => void;
    /** `longPress` only: a held finger opens the context menu on Android, and it is the drag's gesture now. */
    onContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void;
  };
  /** Touch reorder: swap an item with its neighbour and scroll the viewport to follow it. */
  move: (id: string, dir: -1 | 1) => void;
  /**
   * Call right before a reorder made outside a drag (a menu move): re-measures the slots where they stand
   * now, so the glide starts from the current layout even after the page scrolled. `skipId` is left out of
   * that glide (its caller animates it some other way).
   */
  captureLayout: (skipId?: string) => void;
  /** Ref for the portalled drag element that follows the pointer. */
  dragLayerRef: React.RefObject<HTMLDivElement>;
  /** Inline transform that seeds the drag element's position (and survives reorder re-renders). */
  ghostTransform: string;
  /** Width of the grabbed slot, so the drag element matches it. */
  ghostWidth: number;
};

const sameOrder = (a: string[], b: string[]): boolean => a.length === b.length && a.every((x, i) => x === b[i]);

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

/** How far from the top or bottom of the visible band a held card starts the list scrolling, and how fast it
 *  goes at the very edge. Wider and faster than a list of rows: a grid of cards is several screens long, and
 *  a card is carried across it rather than nudged a row at a time. */
const AUTO_SCROLL_ZONE_PX = 140;
const AUTO_SCROLL_MAX_PX_PER_S = 1600;

/** How far past the screen a card still glides; one further off just takes its new place, unseen. */
const GLIDE_MARGIN_PX = 300;

/** Options for `useSortableGrid`. */
export type SortableGridOptions = {
  /**
   * Scroll while a card is held near the top or bottom of what is visible, faster the closer to the edge.
   * Returns the band's insets from the top and bottom of the screen (the chrome, a pinned filter, or a
   * dialog's scroll area), read when the drag starts.
   */
  autoScroll?: () => ScreenInsets;
  /**
   * Also drag by touch, which never sends the drag events a mouse drag runs on: a finger resting on a card
   * for `SORTABLE_ANIM.LONG_PRESS_MS` lifts it, and from then on it moves as a mouse drag moves it (the
   * drag element under the finger, the order changing as it goes, the list scrolling when it is held near
   * an edge of what scrolls it). Until then the touch is an ordinary one: moving past a few pixels, or the
   * browser starting a scroll, gives the press up, so a swipe that starts on a card still scrolls. Give the
   * slots `touch-action: manipulation`. A mouse drags exactly as without it.
   */
  longPress?: boolean;
  /**
   * `longPress` only: a selector for a card's drag handle. A finger pressed on it lifts the card at once, with no
   * hold, as a mouse press on the card does; the rest of the card still lifts by press and hold. Give the handle
   * `touch-action: none`, or the browser takes the touch for a scroll.
   */
  grip?: string;
};

/** A finger on a card (`longPress`): resting on it until the hold lifts the card, then dragging it. */
type TouchDrag = {
  pointerId: number;
  /** The hold completed: the card is up, and follows the finger. */
  lifted: boolean;
  /** Ends the gesture: the hold is cancelled, and the finger no longer followed. */
  stop: () => void;
};

/** Where `scroller` is on screen (the page: the whole screen), as insets from the top and bottom of the screen,
 *  narrowed further by `within` when the caller gives a band of its own. */
const visibleInsets = (scroller: HTMLElement | null, within?: ScreenInsets): ScreenInsets => {
  const box = scroller?.getBoundingClientRect();
  return {
    top: Math.max(box ? box.top : 0, within?.top ?? 0),
    bottom: Math.max(box ? window.innerHeight - box.bottom : 0, within?.bottom ?? 0),
  };
};

// A 1×1 transparent GIF used to SUPPRESS the browser's native drag ghost (we render our own follow
// element instead, so the lifted card is solid + shadowed with no snapshot clipping). Created lazily.
let emptyDragImage: HTMLImageElement | null = null;
const getEmptyDragImage = (): HTMLImageElement => {
  if (!emptyDragImage) {
    emptyDragImage = new Image();
    emptyDragImage.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  }
  return emptyDragImage;
};

/**
 * Drag-to-reorder for a wrapping grid of cards, shared by the banner carousels and the game lists.
 *
 * The order changes in REAL TIME while dragging: on drag-over the dragged card's slot is computed from how
 * many OTHER cards' centres the pointer is past (reading order), and the other cards FLIP-glide out of its
 * way. The native drag ghost is hidden in favour of a portalled element that follows the pointer.
 *
 * A mouse drags with the browser's drag events, which a touch never sends. With `longPress`, a finger lifts
 * a card by resting on it, and the card then moves exactly as a mouse drag moves it. Without it, touch
 * devices reorder with neighbour swaps (`move`) instead.
 */
export const useSortableGrid = (
  ids: string[],
  onReorder: (next: string[]) => void,
  { autoScroll, longPress = false, grip }: SortableGridOptions = {},
): SortableGrid => {
  // The dragged id: a ref read synchronously by the drag-over math + the FLIP pass, mirrored to state only
  // to drive the dimmed-placeholder styling.
  const [draggedId, setDraggedId] = React.useState<string | null>(null);
  const draggedIdRef = React.useRef<string | null>(null);
  // The drag element's positioned wrapper (moved via transform), the grab offset within the card, the
  // card's size, and the last pointer, so the follow survives re-renders.
  const dragLayerRef = React.useRef<HTMLDivElement>(null);
  const grabOffsetRef = React.useRef({ x: 0, y: 0 });
  const ghostSizeRef = React.useRef({ w: 0, h: 0 });
  const lastPointerRef = React.useRef({ x: 0, y: 0 });

  // FLIP: on every order change, glide each slot from its previous position to its new one. In-flight
  // animations are CANCELLED before measuring, so getBoundingClientRect returns the true LAYOUT position
  // (never a mid-glide transform); the dragged placeholder is skipped so it tracks the cursor instantly.
  // `prevRects` (true layout) is also what the drag-over math reads, so reordering never depends on the
  // animation state.
  const slotNodes = React.useRef(new Map<string, HTMLDivElement>());
  const prevRects = React.useRef(new Map<string, DOMRect>());
  // What scrolls the grid (null: the page), found when a drag starts, and its scroll position when the rects
  // were measured. The rects are viewport coordinates, so once the list scrolls under a held card they are
  // off by exactly what it scrolled since: the drag math adds that back rather than measuring again.
  const scrollerRef = React.useRef<HTMLElement | null>(null);
  const rectsScrollRef = React.useRef(0);
  // The scroll `prevRects` were measured at (a layout pass measures again, and records the new one in
  // `rectsScrollRef`; this keeps the old one for the glide).
  const rectsScrollBefore = React.useRef(0);
  // The glides in flight, so a new layout pass cancels only those instead of asking every card for its
  // animations (a long list holds hundreds of cards, and most never glide).
  const gliding = React.useRef(new Map<string, Animation>());
  // The latest ids and callback, for the drag math run outside a render (a frame, the auto-scroll loop).
  const idsRef = React.useRef(ids);
  idsRef.current = ids;
  const onReorderRef = React.useRef(onReorder);
  onReorderRef.current = onReorder;
  // One reorder per frame at most: drag-over fires far more often than the screen draws, and every reorder
  // re-renders the list.
  const frameRef = React.useRef(0);
  // Set by a touch move to the moved id, so the layout pass scrolls the viewport to follow it.
  const pendingScrollRef = React.useRef<string | null>(null);
  // An id the next layout pass must not glide (see `captureLayout`).
  const skipGlideRef = React.useRef<string | null>(null);
  // The finger on a card, held or dragging (`longPress`), or null.
  const touchRef = React.useRef<TouchDrag | null>(null);
  // The slots already listening for a lifted card's finger (see `holdStill`).
  const holdingSlots = React.useRef(new WeakSet<HTMLElement>());

  /** Cancels every glide in flight, so the slots stand where the layout puts them. */
  const cancelGlides = React.useCallback((): void => {
    gliding.current.forEach((animation) => animation.cancel());
    gliding.current.clear();
  }, []);

  /** The slots' layout rects as they stand, with any glide in flight cancelled first. Records the scroll they
   *  were measured at. */
  const measureSlots = React.useCallback((): Map<string, DOMRect> => {
    cancelGlides();
    const rects = new Map<string, DOMRect>();
    slotNodes.current.forEach((el, id) => rects.set(id, el.getBoundingClientRect()));
    rectsScrollRef.current = scrollTopOf(scrollerRef.current);
    return rects;
  }, [cancelGlides]);

  // Keeps a lifted card's finger from scrolling what is under it. Bound to each slot as it registers rather than when
  // the card lifts, because a browser decides at `touchstart` whether a touch's moves can be cancelled at all; it only
  // cancels while a card is actually up.
  const holdStill = React.useCallback((event: TouchEvent): void => {
    if (touchRef.current?.lifted && event.cancelable) event.preventDefault();
  }, []);

  const registerSlot = React.useCallback(
    (id: string) =>
      (el: HTMLDivElement | null): void => {
        if (!el) {
          slotNodes.current.delete(id);
          return;
        }
        slotNodes.current.set(id, el);
        // Once per element: a slot registers again on every render.
        if (longPress && !holdingSlots.current.has(el)) {
          holdingSlots.current.add(el);
          el.addEventListener('touchmove', holdStill, { passive: false });
        }
      },
    [holdStill, longPress],
  );

  // Syncs the DOM (measure + animate the slots) with a new order.
  React.useLayoutEffect(() => {
    const reduce =
      typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    // Reset transforms so we measure layout, not a glide.
    const now = measureSlots();
    // "Scroll with it": transforms are cancelled at this point, so we scroll to the card's true FINAL
    // position (a post-glide read would target the old position and clamp to a no-op).
    const scrollId = pendingScrollRef.current;
    if (scrollId) {
      pendingScrollRef.current = null;
      slotNodes.current.get(scrollId)?.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    }
    const skipId = skipGlideRef.current;
    skipGlideRef.current = null;
    if (!reduce && prevRects.current.size) {
      // The previous rects may be from before the list scrolled: shift them to where they stand now.
      const scrolled = rectsScrollBefore.current - rectsScrollRef.current;
      const onScreen = (top: number, height: number): boolean =>
        top + height > -GLIDE_MARGIN_PX && top < window.innerHeight + GLIDE_MARGIN_PX;
      slotNodes.current.forEach((el, id) => {
        if (id === draggedIdRef.current || id === skipId) return; // the dragged placeholder jumps; the others glide
        const before = prevRects.current.get(id);
        const after = now.get(id);
        if (!before || !after) return;
        const dx = before.left - after.left;
        const dy = before.top + scrolled - after.top;
        // Only what can be seen glides. A long list moves hundreds of cards on one step, and animating the
        // ones off screen costs as much as the ones on it, for nothing.
        if (!(dx || dy) || !(onScreen(after.top, after.height) || onScreen(before.top + scrolled, before.height))) return;
        const animation = el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }], {
          duration: SORTABLE_ANIM.SHIFT_SLOW_MS,
          easing: SORTABLE_ANIM.EASE,
        });
        gliding.current.set(id, animation);
        animation.onfinish = () => {
          if (gliding.current.get(id) === animation) gliding.current.delete(id);
        };
      });
    }
    prevRects.current = now;
    rectsScrollBefore.current = rectsScrollRef.current;
  }, [ids, measureSlots]);

  // Move the drag element to the pointer (imperative transform, no re-render). Records the pointer too, so a
  // reorder re-render re-applies the same position via `ghostTransform`.
  const positionGhost = React.useCallback((clientX: number, clientY: number): void => {
    lastPointerRef.current = { x: clientX, y: clientY };
    const el = dragLayerRef.current;
    if (el) {
      el.style.transform = `translate(${clientX - grabOffsetRef.current.x}px, ${clientY - grabOffsetRef.current.y}px)`;
    }
  }, []);

  // Where the pointer puts the dragged card, as a reorder. Excluding the dragged card from the count gives
  // natural hysteresis (no flicker at edges); the cached true-layout rects keep it stable while cards are
  // mid-glide, and the scroll since they were measured is added back to the pointer.
  const reorderAt = React.useCallback((px: number, viewportY: number): void => {
    const dragId = draggedIdRef.current;
    if (!dragId) return;
    const current = idsRef.current;
    const py = viewportY + (scrollTopOf(scrollerRef.current) - rectsScrollRef.current);
    const others = current.filter((id) => id !== dragId);
    let insertAt = 0;
    for (const id of others) {
      const r = prevRects.current.get(id);
      if (!r) continue;
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const halfH = r.height / 2;
      const pastCentre = py > cy + halfH || (py >= cy - halfH && px > cx);
      if (pastCentre) insertAt += 1;
    }
    const next = [...others.slice(0, insertAt), dragId, ...others.slice(insertAt)];
    if (!sameOrder(next, current)) onReorderRef.current(next);
  }, []);

  /** Runs a reorder still waiting for its frame now (a drop must not lose the last move). */
  const flushReorder = React.useCallback((): void => {
    if (!frameRef.current) return;
    window.cancelAnimationFrame(frameRef.current);
    frameRef.current = 0;
    reorderAt(lastPointerRef.current.x, lastPointerRef.current.y);
  }, [reorderAt]);

  // The dragged card follows the pointer: the drag element at once, the reorder on the next frame.
  const follow = React.useCallback(
    (clientX: number, clientY: number): void => {
      if (!draggedIdRef.current) return;
      positionGhost(clientX, clientY);
      if (frameRef.current) return;
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = 0;
        reorderAt(lastPointerRef.current.x, lastPointerRef.current.y);
      });
    },
    [positionGhost, reorderAt],
  );

  const onGridDragOver = React.useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
      follow(event.clientX, event.clientY);
    },
    [follow],
  );

  const endDrag = React.useCallback((): void => {
    flushReorder();
    draggedIdRef.current = null;
    setDraggedId(null);
  }, [flushReorder]);
  React.useEffect(
    () => () => {
      window.cancelAnimationFrame(frameRef.current);
      touchRef.current?.stop();
    },
    [],
  );

  // Auto-scroll: while a card is held, a frame loop scrolls whenever the pointer sits near the top or bottom
  // of the visible band, faster the closer it is, and re-reads the drop place after each step (the pointer
  // has not moved, the list has). The browser's own drag scrolling only starts at the very edge, and crawls.
  // A mouse drag scrolls only where the caller asks (`autoScroll`); a held finger has no other way to scroll at
  // all, so it always does, within what scrolls the grid as it stands on screen.
  const dragging = draggedId !== null;
  React.useEffect(() => {
    if (!dragging) return undefined;
    const insets = touchRef.current?.lifted ? visibleInsets(scrollerRef.current, autoScroll?.()) : autoScroll?.();
    if (!insets) return undefined;
    let frame = 0;
    let last = 0;
    let carry = 0;
    const tick = (now: number): void => {
      const elapsed = last ? Math.min(now - last, 50) : 0;
      last = now;
      const top = insets.top + AUTO_SCROLL_ZONE_PX;
      const bottom = window.innerHeight - insets.bottom - AUTO_SCROLL_ZONE_PX;
      const y = lastPointerRef.current.y;
      let speed = 0;
      if (y < top) speed = -clamp((top - y) / AUTO_SCROLL_ZONE_PX, 0, 1);
      else if (y > bottom) speed = clamp((y - bottom) / AUTO_SCROLL_ZONE_PX, 0, 1);
      // Eased in, so the edge of the zone creeps and the very edge races.
      carry = speed === 0 ? 0 : carry + (Math.sign(speed) * speed * speed * AUTO_SCROLL_MAX_PX_PER_S * elapsed) / 1000;
      const step = Math.trunc(carry);
      if (step !== 0) {
        carry -= step;
        const scroller = scrollerRef.current;
        const before = scrollTopOf(scroller);
        // 'instant' so a page-wide `scroll-behavior: smooth` cannot queue the steps up behind the pointer.
        if (scroller) scroller.scrollBy({ top: step, behavior: 'instant' });
        else window.scrollBy({ top: step, behavior: 'instant' });
        if (scrollTopOf(scroller) !== before) reorderAt(lastPointerRef.current.x, y);
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [dragging, autoScroll, reorderAt]);

  /** Picks card `id` up from `slot` under the pointer at (clientX, clientY), by mouse or by finger. */
  const lift = React.useCallback(
    (id: string, slot: HTMLElement, clientX: number, clientY: number): void => {
      const rect = slot.getBoundingClientRect();
      grabOffsetRef.current = { x: clientX - rect.left, y: clientY - rect.top };
      ghostSizeRef.current = { w: rect.width, h: rect.height };
      lastPointerRef.current = { x: clientX, y: clientY };
      scrollerRef.current = scrollParentOf(slot);
      // Refresh the reorder baseline to the CURRENT layout: the cached rects go stale after a page scroll
      // (they're only re-measured on a reorder), and a fresh grab would otherwise jump on a 1px move.
      prevRects.current = measureSlots();
      rectsScrollBefore.current = rectsScrollRef.current;
      draggedIdRef.current = id;
    },
    [measureSlots],
  );

  // Press and hold (`longPress`): a finger resting on a card lifts it, then drags it. The rest of the gesture is
  // followed on the window rather than on the slot, which a reorder moves in the DOM under the finger.
  const onSlotPointerDown = React.useCallback(
    (id: string) =>
      (event: React.PointerEvent<HTMLDivElement>): void => {
        // A mouse drags with the drag events. One finger at a time, and never while a card is already up.
        if (event.pointerType === 'mouse' || !event.isPrimary || touchRef.current || draggedIdRef.current) return;
        // A press on a control in the card (its remove button) belongs to that control.
        if (event.target instanceof Element && event.target.closest(CONTROLS)) return;
        const slot = event.currentTarget;
        const { pointerId } = event;
        const start = { x: event.clientX, y: event.clientY };
        const at = { ...start };
        const touch: TouchDrag = { pointerId, lifted: false, stop: () => undefined };
        const onMove = (move: PointerEvent): void => {
          if (move.pointerId !== pointerId) return;
          at.x = move.clientX;
          at.y = move.clientY;
          if (touch.lifted) follow(move.clientX, move.clientY);
          // Moving before the hold completes is a scroll: the press is given up, and the browser keeps the touch.
          else if (Math.hypot(move.clientX - start.x, move.clientY - start.y) > LONG_PRESS_SLOP_PX) touch.stop();
        };
        // Lifting the finger drops the card where it is; the browser taking the touch over ends the drag the same
        // way (the order already follows the card, as a mouse drag's does).
        const onEnd = (end: PointerEvent): void => {
          if (end.pointerId !== pointerId) return;
          const { lifted } = touch;
          touch.stop();
          if (lifted) endDrag();
        };
        // A finger on the card's handle (`grip`) lifts it at once; anywhere else on the card, a resting one does.
        const atOnce = grip !== undefined && event.target instanceof Element && event.target.closest(grip) !== null;
        const hold = atOnce
          ? 0
          : window.setTimeout(() => {
              // The card may have left the grid while the finger rested on it.
              if (!slot.isConnected || !idsRef.current.includes(id) || draggedIdRef.current) {
                touch.stop();
                return;
              }
              touch.lifted = true;
              lift(id, slot, at.x, at.y);
              setDraggedId(id);
              navigator.vibrate?.(10);
            }, SORTABLE_ANIM.LONG_PRESS_MS);
        if (atOnce) {
          touch.lifted = true;
          lift(id, slot, at.x, at.y);
          setDraggedId(id);
        }
        touch.stop = (): void => {
          window.clearTimeout(hold);
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onEnd);
          window.removeEventListener('pointercancel', onEnd);
          if (touchRef.current === touch) touchRef.current = null;
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onEnd);
        window.addEventListener('pointercancel', onEnd);
        touchRef.current = touch;
      },
    [endDrag, follow, grip, lift],
  );

  const slotDragProps = React.useCallback(
    (id: string) => ({
      onDragStart: (event: React.DragEvent<HTMLDivElement>): void => {
        // A browser that turns a held finger into a drag of its own must not start a second one: the finger's
        // own drag is already running.
        if (touchRef.current) {
          event.preventDefault();
          return;
        }
        lift(id, event.currentTarget, event.clientX, event.clientY);
        if (event.dataTransfer) {
          event.dataTransfer.effectAllowed = 'move';
          event.dataTransfer.setData('text/plain', id); // Firefox needs data to start a drag
          event.dataTransfer.setDragImage(getEmptyDragImage(), 0, 0);
        }
        setDraggedId(id);
      },
      onDrag: (event: React.DragEvent<HTMLDivElement>): void => {
        if (event.clientX === 0 && event.clientY === 0) return; // ignore the terminal 0,0 drag event
        positionGhost(event.clientX, event.clientY);
      },
      onDragEnd: endDrag,
      ...(longPress
        ? {
            onPointerDown: onSlotPointerDown(id),
            onContextMenu: (event: React.MouseEvent<HTMLDivElement>): void => {
              if (touchRef.current) event.preventDefault();
            },
          }
        : {}),
    }),
    [endDrag, lift, longPress, onSlotPointerDown, positionGhost],
  );

  const move = React.useCallback(
    (id: string, dir: -1 | 1): void => {
      const i = ids.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= ids.length) return;
      const next = [...ids];
      [next[i], next[j]] = [next[j], next[i]];
      pendingScrollRef.current = id;
      onReorder(next);
    },
    [ids, onReorder],
  );

  const captureLayout = React.useCallback(
    (skipId?: string): void => {
      prevRects.current = measureSlots();
      rectsScrollBefore.current = rectsScrollRef.current;
      skipGlideRef.current = skipId ?? null;
    },
    [measureSlots],
  );

  // Seed position for the drag element on the render it first appears: under the pointer, using the cached
  // grab offset, so it never flashes at 0,0.
  const ghostTransform = `translate(${lastPointerRef.current.x - grabOffsetRef.current.x}px, ${
    lastPointerRef.current.y - grabOffsetRef.current.y
  }px)`;

  return {
    draggedId,
    registerSlot,
    onGridDragOver,
    slotDragProps,
    move,
    captureLayout,
    dragLayerRef,
    ghostTransform,
    ghostWidth: ghostSizeRef.current.w,
  };
};
